import { ReactElement, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';
import Pagination from '../../components/Pagination';
import {
  FileText,
  ChevronLeft,
  Loader2,
  BarChart3,
  TrendingUp,
  LineChart,
} from 'lucide-react';

function reportAnalysisId(path: string): string {
  // Path may be absolute (https://api.finexus.net/reports/{id}) or relative (/reports/{id}).
  return path.replace(/^.*\/reports\//, '');
}

// API base URL - use proxy in development (empty string), or REACT_APP_API_URL in production
const API_BASE = process.env.REACT_APP_API_URL || '';

// Types matching API response
interface ArticleSummary {
  id: number;
  title: string;
  subtitle: string;
  tickers: string[];
  category: string;
  section_number: number;
  section_name: string;
  provider: string;
  status: string;
  generated_at: string;
  time_ago: string;
  path: string;
}

// Category configuration
const CATEGORY_CONFIG: Record<string, {
  icon: ReactElement;
  color: string;
  bgColor: string;
  borderColor: string;
  name: string;
  description: string;
  sectionNumbers: number[];
}> = {
  overview: {
    icon: <TrendingUp className="w-6 h-6" />,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    name: 'Overview',
    description: 'High-level insights and investment thesis',
    sectionNumbers: [1, 18],
  },
  classification: {
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    name: 'Company Profile',
    description: 'Factor tags, style, and competitive positioning',
    sectionNumbers: [2],
  },
  historical: {
    icon: <LineChart className="w-6 h-6" />,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    name: 'Financial History',
    description: 'Multi-year revenue, profitability, and efficiency trends',
    sectionNumbers: [3],
  },
  fundamentals: {
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    name: 'Deep Dive Fundamentals',
    description: 'Detailed financial statement and valuation analysis',
    sectionNumbers: [4, 5, 6],
  },
  valuation: {
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    name: 'Valuation',
    description: 'Valuation analysis and peer comparison',
    sectionNumbers: [7],
  },
  macro: {
    icon: <TrendingUp className="w-6 h-6" />,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    name: 'Macroeconomic Context',
    description: 'Economic indicators, rates, inflation, and GDP trends',
    sectionNumbers: [8],
  },
  risk: {
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    name: 'Risk Assessment',
    description: 'Financial health screening and risk alerts',
    sectionNumbers: [11],
  },
  market: {
    icon: <TrendingUp className="w-6 h-6" />,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    name: 'Stocks and Market',
    description: 'Returns, risk profile, and price behavior',
    sectionNumbers: [9],
  },
  institutional: {
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    name: 'Institutional & Insider Activity',
    description: 'Institutional ownership trends, position flows, and insider trading patterns',
    sectionNumbers: [10],
  },
  sentiment: {
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    name: 'Predictive Signals',
    description: 'Predictive signal analysis, institutional ownership dynamics, and insider activity',
    sectionNumbers: [12, 13, 14],
  },
  context: {
    icon: <LineChart className="w-6 h-6" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    name: 'Context',
    description: 'Industry, competitive, and ESG factors',
    sectionNumbers: [15, 16, 17],
  },
};

// Report Card Component
interface ReportCardProps {
  report: ArticleSummary;
  categoryKey: string;
}

function ReportCard({ report, categoryKey }: ReportCardProps): ReactElement {
  const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.overview;
  const analysisId = reportAnalysisId(report.path);

  // Check if report is new (within 7 days)
  const isNew = report.generated_at ?
    (Date.now() - new Date(report.generated_at).getTime()) < 7 * 24 * 60 * 60 * 1000 : false;

  // Format date
  const dateStr = report.generated_at ?
    new Date(report.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className={`group relative bg-white border ${config.borderColor} rounded-lg overflow-hidden hover:shadow-md hover:border-slate-300 transition-all duration-200`}>
      <a href={report.path} className="block">
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3 pr-8">
            {isNew && (
              <span className="px-2 py-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-100 rounded">
                NEW
              </span>
            )}
            <span className="text-xs text-slate-400">{report.time_ago || dateStr}</span>
          </div>

        {/* Title */}
        <h3 className="text-base font-semibold text-slate-800 group-hover:text-slate-900 transition-colors line-clamp-2 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
          {report.title}
        </h3>

        {/* Description */}
        {report.subtitle && (
          <p className="text-sm text-slate-500 line-clamp-2 mb-4">
            {report.subtitle}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1.5 flex-wrap">
            {report.tickers.slice(0, 4).map((ticker) => (
              <span
                key={ticker}
                className="px-2 py-0.5 text-[11px] font-mono font-medium text-slate-600 bg-slate-100 rounded"
              >
                {ticker}
              </span>
            ))}
            {report.tickers.length > 4 && (
              <span className="text-[11px] text-slate-400">
                +{report.tickers.length - 4}
              </span>
            )}
          </div>
        </div>
      </div>
      </a>
      <div className="absolute top-3 right-3">
        <BookmarkButton
          itemType="report"
          itemId={analysisId}
          title={report.title}
          subtitle={report.tickers?.join(', ')}
          url={report.path}
          size="sm"
        />
      </div>
    </div>
  );
}

// Main Component
export default function CategoryList(): ReactElement {
  const { category } = useParams<{ category: string }>();
  const categoryKey = category || 'overview';
  const limit = 24;

  const [reports, setReports] = useState<ArticleSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.overview;
  const totalPages = Math.ceil(totalCount / limit);
  const hasMore = (page + 1) * limit < totalCount;

  useEffect(() => {
    setIsLoading(true);
    const offset = page * limit;
    fetch(`${API_BASE}/api/articles/list?category=${categoryKey}&limit=${limit}&offset=${offset}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load reports');
        return res.json();
      })
      .then((data: { articles: ArticleSummary[]; total_count: number }) => {
        setReports(data.articles);
        setTotalCount(data.total_count);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [categoryKey, page]);

  // Reset page when category changes
  useEffect(() => {
    setPage(0);
  }, [categoryKey]);

  const batchItems = reports.map((r) => ({
    itemType: 'report' as const,
    itemId: reportAnalysisId(r.path),
  }));

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Back Link */}
          <Link
            to="/reports"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Reports
          </Link>

          {/* Header */}
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-xl ${config.bgColor} ${config.color} flex items-center justify-center`}>
              {config.icon}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Georgia, serif' }}>
                {config.name}
              </h1>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                {config.description}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <span className="px-2.5 py-1 text-xs font-medium text-slate-500 bg-slate-100 rounded-full">
                  {totalCount} reports
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-slate-400 mb-4" />
            <p className="text-slate-500">Loading reports...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <FileText className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">Failed to load reports</h3>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        )}

        {/* Reports Grid */}
        {!isLoading && !error && reports.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reports.map((report) => (
                <ReportCard key={report.id} report={report} categoryKey={categoryKey} />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              hasMore={hasMore}
            />
          </>
        )}

        {/* Empty State */}
        {!isLoading && !error && reports.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">No reports in this category</h3>
            <p className="text-sm text-slate-500">Check back later for new reports</p>
          </div>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
